import { ENDORSEMENT_REASONS, ENDORSEMENT_TYPES } from '@domain/endorsements/constants';
import { EndorsementPremiumChange } from '@root/@types/types';
import { isEmptyValue } from '@root/helpers';
import dayjs from 'dayjs';
import { z } from 'zod';

function isConditionalPremiumOption(premium_change?: EndorsementPremiumChange) {
  return Boolean(
    premium_change && [EndorsementPremiumChange.ADDITION, EndorsementPremiumChange.RETURN].includes(premium_change),
  );
}

const baseSchema = z.object({
  // Step 0 (standalone only)
  parentSubmissionId: z.string().optional(),

  // Step 1 - Basic Info
  reference: z.string().min(1, { message: 'Reference is required' }),
  internal_description: z.string().optional(),
  type: z.enum(ENDORSEMENT_TYPES, { required_error: 'Type is required' }),
  effective_date: z
    .string()
    .min(1, { message: 'Effective date is required' })
    .refine((value) => dayjs(value).isValid(), { message: 'Invalid date format' }),
  expiry_date: z
    .string()
    .min(1, { message: 'Expiry date is required' })
    .refine((value) => dayjs(value).isValid(), { message: 'Invalid date format' }),

  // Step 2 - Reason & Premium
  reason: z.enum(ENDORSEMENT_REASONS, { required_error: 'Reason is required' }),
  premium_change: z.nativeEnum(EndorsementPremiumChange),
  additional_premium: z
    .object({
      amount: z.number(),
      currency: z.string(),
    })
    .nullable()
    .optional(),
  endorsement_summary_template_id: z.number(),
});

export const validationSchema = baseSchema.superRefine((data, ctx) => {
  const hasAdditionalPremium =
    !isEmptyValue(data.additional_premium?.amount) && !isEmptyValue(data.additional_premium?.currency);

  if (isConditionalPremiumOption(data.premium_change) && !hasAdditionalPremium) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Additional premium is required',
      path: ['additional_premium'],
    });
  }
});

export const createEndorsementStep0Schema = baseSchema.pick({ parentSubmissionId: true }).extend({
  parentSubmissionId: z.string().min(1, { message: 'Parent contract is required' }),
});

export const createEndorsementStep1Schema = baseSchema.pick({
  endorsement_summary_template_id: true,
});

export const createEndorsementStep2Schema = baseSchema.pick({
  reference: true,
  internal_description: true,
  type: true,
  effective_date: true,
  expiry_date: true,
});

export const createEndorsementStep3Schema = baseSchema.pick({
  reason: true,
  premium_change: true,
  additional_premium: true,
});
